import { Component, OnInit, inject, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink, RouterLinkActive, Router } from '@angular/router';
import { MenuItem, MessageService } from 'primeng/api';
import { Menu } from 'primeng/menu';
import { ButtonModule } from 'primeng/button';
import { AvatarModule } from 'primeng/avatar';
import { UserService } from '@app/auth/services/user.service';
import { AuthService } from '@app/auth/services/auth.service';
import { AuthRedirectService } from '@app/auth/services/auth-redirect.service';
import { UtilizadoresService } from '../components/api/services/utilizadores.service';

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [
    CommonModule,
    RouterLink,
    RouterLinkActive,
    ButtonModule,
    Menu,
    AvatarModule,
  ],
  providers: [UtilizadoresService],
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss',
})
export class HeaderComponent implements OnInit {
  IsAuthenticad: boolean = false;
  ShowOpenMenu: boolean = false;
  IsprofileComplet: boolean = false;
  initials: string = '';
  Username: string = '';
  items: MenuItem[] = [];
  utilizador: any;
  isScrolled: boolean = false;

  private readonly userService = inject(UserService);
  private readonly authService = inject(AuthService);
  private readonly authRedirectService = inject(AuthRedirectService);
  private readonly utilizadoresService = inject(UtilizadoresService);
  private readonly messageService = inject(MessageService);
  private readonly router = inject(Router);

  private fieldLabels: Record<string, string> = {
    id: 'Identificador',
    userName: 'Nome de utilizador',
    email: 'Email',
    dataCriacao: 'Data de criação',
    dataCriacaoFormatada: 'Data de criação',
    ativo: 'Estado da conta',
    dataNascimento: 'Data de nascimento',
    iban: 'IBAN',
    telefone: 'Telefone',
    tipoDocumentoENUM: 'Tipo de documento',
    tipoDocumento: 'Tipo de documento',
    numeroDocumento: 'Número do documento',
  };

  constructor() {
    this.setupUserMenu();
  }

  @HostListener('window:scroll')
  onWindowScroll() {
    this.isScrolled = window.scrollY > 20;
  }

  get isAdmin(): boolean {
    // 1. Verificação pelas roles vindas da API
    if (this.utilizador?.roles && Array.isArray(this.utilizador.roles)) {
      const hasAdminRole = this.utilizador.roles.some(
        (r: any) =>
          (typeof r === 'string' && r.toLowerCase() === 'admin') ||
          (r?.roleName && r.roleName.toLowerCase() === 'admin'),
      );
      if (hasAdminRole) return true;
    }

    // 2. Verificação pelas claims do JWT
    const claims = this.authService.getClaims();
    if (claims) {
      const roleClaim =
        claims.role ||
        claims.roles ||
        claims['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'];

      if (Array.isArray(roleClaim)) {
        return roleClaim.some(
          (r) => typeof r === 'string' && r.toLowerCase() === 'admin',
        );
      }
      if (typeof roleClaim === 'string') {
        return roleClaim.toLowerCase() === 'admin';
      }
    }

    return false;
  }

  setupUserMenu(): void {
    const menuItems: MenuItem[] = [];

    // Se for Administrador, exibe o link específico para a gestão de Administrador
    if (this.isAdmin) {
      menuItems.push({
        label: 'Administrador',
        icon: 'pi pi-shield',
        command: () => this.goToAdmin(),
      });
    }

    // Link para a Área do Utilizador (contendo Perfil e Dashboards)
    menuItems.push({
      label: 'Área do Utilizador',
      icon: 'pi pi-user',
      command: () => this.goToUserArea(),
    });

    // Terminar Sessão (Logout)
    menuItems.push({
      label: 'Terminar Sessão',
      icon: 'pi pi-sign-out',
      command: () => this.logout(),
    });

    this.items = menuItems;
  }

  goToAdmin(): void {
    this.router.navigate(['/admin']);
  }

  goToUserArea(): void {
    this.router.navigate(['/dashboads']);
  }

  ngOnInit(): void {
    this.authService.isAuthenticatedChange().subscribe((isAuth) => {
      this.IsAuthenticad = isAuth;
      if (isAuth) {
        this.loadUserData();
      } else {
        this.Username = '';
        this.initials = '';
        this.utilizador = null;
        this.IsprofileComplet = false;
        this.setupUserMenu();
      }
    });
  }

  loadUserData(): void {
    this.Username = this.userService.getUsername() ?? '';
    this.initials = this.userService.getUserInitials() ?? '';
    this.getUser();
    this.checkProfileUSer();
    this.setupUserMenu();
  }

  onSearch(event: any) {
    const val = event.target?.value?.trim();
    if (val) {
      this.router.navigate(['/campanhas'], { queryParams: { q: val } });
    }
  }

  openLogin(returnUrl?: string): void {
    if (returnUrl) {
      this.authRedirectService.redirectToLogin(returnUrl);
    } else {
      this.authRedirectService.redirectToLogin();
    }
  }

  openRegister(returnUrl?: string): void {
    if (returnUrl) {
      this.authRedirectService.redirectToRegister(returnUrl);
    } else {
      this.authRedirectService.redirectToRegister();
    }
  }

  HideShowMenuMobile() {
    this.scrollToTop();
    this.ShowOpenMenu = !this.ShowOpenMenu;
  }

  scrollToTop(duration = 300) {
    const start = window.scrollY;
    const startTime = performance.now();
    const step = (now: number) => {
      const t = Math.min(1, (now - startTime) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      window.scrollTo(0, Math.round(start * (1 - eased)));
      if (t < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }

  logout() {
    const accessToken: string | null = this.authService.getAccessToken();

    if (!accessToken) {
      this.authService.clearSession();
      return;
    }
    this.authService.logout();
  }

  callRouteCreateCampanha() {
    if (!this.authService.isLoggedIn()) {
      this.openLogin('/criar-campanha');
      return;
    }

    this.checkProfileUSer();
    if (this.IsprofileComplet) {
      this.router.navigate(['/criar-campanha/']);
    } else if (this.IsAuthenticad) {
      this.perfilIncomplete(this.utilizador);
      return;
    } else {
      this.router.navigate(['/criar-campanha/']);
    }
  }

  checkProfileUSer() {
    const userId = this.userService.getUserId();
    if (!userId) {
      this.IsprofileComplet = false;
      return;
    }
    this.utilizadoresService.checkPerfilUser(userId).subscribe({
      next: (res: any) => {
        if (res?.retorno?.codigo === 200) {
          this.IsprofileComplet = res?.data;
        } else {
          this.IsprofileComplet = false;
        }
      },
    });
  }

  perfilIncomplete(user: any): boolean {
    if (!user) return true;
    const requiredFields = [
      'id',
      'userName',
      'email',
      'dataCriacao',
      'ativo',
      'dataNascimento',
      'iban',
      'telefone',
      'tipoDocumentoENUM',
      'numeroDocumento',
    ];

    const missingFields = requiredFields.filter((field) => {
      const value = user[field];

      return (
        value === null ||
        value === undefined ||
        (typeof value === 'string' && value.trim() === '') ||
        (Array.isArray(value) && value.length === 0)
      );
    });

    if (missingFields.length) {
      const friendlyNames = missingFields.map(
        (field) => this.fieldLabels[field] || field,
      );

      this.messageService.add({
        severity: 'warn',
        summary: 'Perfil incompleto',
        detail: `Por favor complete: ${friendlyNames.join(', ')}`,
        life: 5000,
      });

      return true;
    }

    return false;
  }

  getUser(): void {
    const userId = this.userService.getUserId();
    if (userId) {
      this.utilizadoresService.show(userId).subscribe({
        next: (res) => {
          this.utilizador = res.data;
          if (this.utilizador?.userName && !this.Username) {
            this.Username = this.utilizador.userName;
          }
          this.setupUserMenu();
        },
        error: (err) => {
          console.error(err);
        },
      });
    }
  }
}
